/**
 * useAIRegeneration Hook for LOOPINO AI Mobile
 * Handles AI content regeneration for mobile posts
 */

import { useState, useCallback } from 'react';
import { useAuthContext } from '../context/AuthContext';
import { apiFetch, buildResponseError, parseResponseBody } from '../utils/http';

export const useAIRegeneration = () => {
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerationError, setRegenerationError] = useState(null);
  const [showRegenerationModal, setShowRegenerationModal] = useState(false);
  const [regenerationType, setRegenerationType] = useState(null);
  const [currentPostId, setCurrentPostId] = useState(null);

  const { user } = useAuthContext();

  // Available regeneration types
  const regenerationTypes = {
    text: {
      id: 'text',
      title: 'Rigenera Testo',
      description: 'Nuovo contenuto mantenendo il tone',
      icon: '📝',
      endpoint: '/ai/regenerate/text'
    },
    image: {
      id: 'image', 
      title: 'Nuova Immagine',
      description: 'Genera immagine diversa con stesso prompt',
      icon: '🖼️',
      endpoint: '/ai/regenerate/image'
    },
    style: {
      id: 'style',
      title: 'Cambia Stile',
      description: 'Modifica lo stile visivo',
      icon: '🎨',
      endpoint: '/ai/regenerate/style'
    },
    perspective: {
      id: 'perspective',
      title: 'Altra Prospettiva', 
      description: 'Cambia angolo/composizione',
      icon: '📸',
      endpoint: '/ai/regenerate/perspective'
    },
    hashtags: {
      id: 'hashtags',
      title: 'Nuovi Hashtag',
      description: 'Rigenera hashtag trending',
      icon: '🏷️',
      endpoint: '/ai/regenerate/hashtags'
    },
    timing: {
      id: 'timing',
      title: 'Ottimizza Orario',
      description: 'Suggerisci orario migliore', 
      icon: '⏰',
      endpoint: '/ai/optimize/timing'
    }
  };

  const openRegenerationModal = useCallback((postId, type = null) => {
    setCurrentPostId(postId);
    setRegenerationType(type);
    setShowRegenerationModal(true);
    setRegenerationError(null);
  }, []);

  const closeRegenerationModal = useCallback(() => {
    setShowRegenerationModal(false);
    setRegenerationType(null);
    setCurrentPostId(null);
    setRegenerationError(null);
  }, []);

  const regenerateContent = useCallback(async (postId, type, options = {}) => {
    if (!postId || !type || !regenerationTypes[type]) {
      throw new Error('Invalid regeneration parameters');
    }

    setIsRegenerating(true);
    setRegenerationError(null);

    try {
      const endpoint = regenerationTypes[type].endpoint;

      console.log(`[AI Regeneration] Starting ${type} regeneration for post ${postId}`);

      const response = await apiFetch(`/api/v1${endpoint}`, {
        method: 'POST',
        data: {
          post_id: postId,
          type: type,
          options: options,
          user_preferences: {
            tone: user?.preferences?.tone || 'professional',
            style: user?.preferences?.style || 'modern'
          }
        }
      });

      if (!response.ok) {
        const errorData = await parseResponseBody(response);
        throw buildResponseError(response, errorData);
      }

      const result = await parseResponseBody(response);
      
      console.log(`[AI Regeneration] ${type} regeneration completed:`, result);
      
      // Close modal on success
      closeRegenerationModal();
      
      return result;
      
    } catch (error) {
      console.error(`[AI Regeneration] Error during ${type} regeneration:`, error);
      setRegenerationError(error.message);
      throw error;
    } finally {
      setIsRegenerating(false);
    }
  }, [user, closeRegenerationModal]);

  const regenerateMultiple = useCallback(async (postId, types) => {
    const results = {};
    const errors = {};
    
    for (const type of types) {
      try {
        results[type] = await regenerateContent(postId, type);
      } catch (error) {
        errors[type] = error.message;
      }
    }
    
    return { results, errors };
  }, [regenerateContent]);

  const clearError = useCallback(() => {
    setRegenerationError(null);
  }, []);

  return {
    // State
    isRegenerating,
    regenerationError,
    showRegenerationModal,
    regenerationType,
    currentPostId,
    
    // Available types
    regenerationTypes,
    regenerationTypesList: Object.values(regenerationTypes),
    
    // Modal controls
    openRegenerationModal,
    closeRegenerationModal,
    
    // Regeneration methods
    regenerateContent,
    regenerateMultiple,
    
    // Quick regeneration methods
    regenerateText: (postId, options) => regenerateContent(postId, 'text', options),
    regenerateImage: (postId, options) => regenerateContent(postId, 'image', options),
    regenerateHashtags: (postId, options) => regenerateContent(postId, 'hashtags', options),
    optimizeTiming: (postId, options) => regenerateContent(postId, 'timing', options),
    
    // Utilities
    clearError,
    getRegenerationType: (type) => regenerationTypes[type] || null
  };
}; 
